import React, { useRef, useState } from 'react';
import Picture from '../../../components/01_atoms/DefaultPicture';
import Popup from '../../Popup';
import Slider from '../../../components/01_atoms/Slider';
import ArrowDownIcon from '../../../public/static/icons/arrow-down.svg';
import { pushSliderHasChanged as gtmPushSliderHasChanged } from '../../../utils/gtm';
import { DefaultPictureType } from '../../../types';
import { generateClassNameByBehaviorSettings } from '../../../utils/behaviorSettings';
import SlickSlider, { LazyLoadTypes } from 'react-slick';
import { BehaviourSettingsType } from '../../../utils/behaviorSettings/prop-types';

import s from './index.module.scss';

type Image = {
  image: DefaultPictureType;
  imageFull: DefaultPictureType;
};

type Props = {
  heading?: string;
  images: Image[];
  behaviorSettings?: BehaviourSettingsType;
  uuid?: string;
};

type ArrowProps = {
  onClick: () => void;
  type: 'next' | 'prev';
};

const defaultLightboxSliderSettings = {
  dots: false,
  infinite: true,
  lazyLoad: 'ondemand' as LazyLoadTypes,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  centerMode: true,
  centerPadding: '0',
  initialSlide: 0,
  afterChange: (index: number) => undefined,
};

const defaultMobileLightboxSliderSettings = {
  dots: true,
  infinite: true,
  lazyLoad: 'progressive' as LazyLoadTypes,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  centerMode: false,
  centerPadding: '0',
  variableWidth: true,
  initialSlide: 0,
  afterChange: (index: number) => undefined,
};

const ArrowButton = ({ onClick, type }: ArrowProps) => (
  <button
    tabIndex={0}
    className={`${s['bb-santa-letter-line-gallery__arrow']} ${s[`bb-santa-letter-line-gallery__arrow--${type}`]}`}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
  >
    <ArrowDownIcon />
  </button>
);

const BBSantaLetterLineGallery = ({ images, heading = '', uuid, behaviorSettings }: Props) => {
  const [slideIndex, setSlideIndex] = useState(-1);

  // Desktop.
  const lightboxSliderSettings = defaultLightboxSliderSettings;
  const lightboxSlider = useRef<SlickSlider>(null);
  lightboxSliderSettings.initialSlide = slideIndex;
  lightboxSliderSettings.afterChange = (index) => {
    const newIndex = Math.round(index);
    setSlideIndex(newIndex);

    gtmPushSliderHasChanged({
      uuid,
      type: 'gifts_santa_letter_line_gallery',
      current_item: newIndex,
    });
  };

  // Mobile.
  const mobilelightboxSliderSettings = defaultMobileLightboxSliderSettings;
  mobilelightboxSliderSettings.afterChange = (index) => {
    const newIndex = Math.round(index);

    gtmPushSliderHasChanged({
      uuid,
      type: 'gifts_santa_letter_line_gallery',
      current_item: newIndex,
    });
  };

  const onNextClick = () => {
    if (lightboxSlider.current) {
      lightboxSlider.current.slickNext();
    }
  };
  const onPrevClick = () => {
    if (lightboxSlider.current) {
      lightboxSlider.current.slickPrev();
    }
  };

  const classes = [
    'bb',
    'bb-santa-letter-line-gallery',
    s['bb-santa-letter-line-gallery'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div className={classes.join(' ')} id={uuid}>
      <div className="container">
        <div className={s['bb-santa-letter-line-gallery__content']}>
          {heading && <h3 className={s['bb-santa-letter-line-gallery__heading']}>{heading}</h3>}
          <div className={s['bb-santa-letter-line-gallery__images']}>
            {images.map((item, index) => (
              <Picture
                {...item.image}
                key={item.image.fallbackSrc}
                onClick={() => setSlideIndex(index)}
                onKeyDown={(e) => e.key === 'Enter' && setSlideIndex(index)}
                tabIndex={0}
                className={s['bb-santa-letter-line-gallery__image']}
              />
            ))}
          </div>
        </div>
        <Slider
          {...mobilelightboxSliderSettings}
          className={s['bb-santa-letter-line-gallery__mobile']}
        >
          {images.map((item, index) => (
            <div key={`gifts-santa-letter-line-gallery-slide-${index}`}>
              <div className={s['bb-santa-letter-line-gallery__mobile-slide']}>
                <Picture
                  {...item.image}
                  onClick={() => setSlideIndex(index)}
                  onKeyDown={(e) => e.key === 'Enter' && setSlideIndex(index)}
                  className={s['bb-santa-letter-line-gallery__mobile-image']}
                />
              </div>
            </div>
          ))}
        </Slider>
      </div>
      {slideIndex > -1 && (
        <Popup
          onRequestClose={() => setSlideIndex(-1)}
          isOpen={true}
          overlayClassName={`${s['bb-santa-letter-line-gallery__overlay']} ${s['bb-santa-letter-line-gallery__overlay-important']}`}
          className={s['bb-santa-letter-line-gallery__modal']}
        >
          <Slider {...lightboxSliderSettings} ref={lightboxSlider}>
            {images.map((item, i) => (
              <div key={`gifts-santa-letter-line-gallery-slide-${i}`}>
                <div className={s['bb-santa-letter-line-gallery__lightbox-slide']}>
                  <Picture
                    {...item.imageFull}
                    className={s['bb-santa-letter-line-gallery__lightbox-image']}
                  />
                </div>
              </div>
            ))}
          </Slider>
          <div className={s['bb-santa-letter-line-gallery__lightbox-arrows']}>
            <ArrowButton onClick={onPrevClick} type="prev" />
            <ArrowButton onClick={onNextClick} type="next" />
          </div>
        </Popup>
      )}
    </div>
  );
};

export default BBSantaLetterLineGallery;
